// Shared UI primitives for the superadmin organisation-detail tabs.
// Kept in one place so the four tab files (Grupper, Användare, Anläggningar,
// Inställningar) stay lean and visually consistent.
import React from "react";
import { Box, Typography, InputBase, Skeleton } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

export const INK = "#0a1628";
export const INK_MUTED = "#5f6877";
export const LINE = "#e6e4df";
export const LINE_SOFT = "#eeece7";
export const BRAND = "#8a1125";
export const NAVY = "#10324a";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const gradientFor = (name = "") =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

export const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const Avatar = ({ name = "?", size = 44 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "13px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 10px 22px -12px ${a}55`,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

export const SearchBar = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      px: 2,
      height: 44,
      flex: { xs: "1 1 100%", sm: "0 1 320px" },
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.28)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      sx={{
        flex: 1,
        fontSize: "14px",
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

export const PrimaryButton = ({
  icon: Icon = AddRoundedIcon,
  label,
  onClick,
  disabled,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.25,
      px: 2.5,
      py: 1.5,
      background: INK,
      color: "#fff",
      border: "none",
      borderRadius: "12px",
      fontFamily: '"DM Sans", sans-serif',
      fontSize: "14px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      transition: "all 0.25s ease",
      boxShadow: disabled ? "none" : "0 14px 32px -16px rgba(10,22,40,0.5)",
      "&:hover": disabled
        ? {}
        : { background: "#1a2638", transform: "translateY(-1px)" },
    }}
  >
    <Icon sx={{ fontSize: 18 }} />
    {label}
  </Box>
);

export const ToolbarRow = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
      mb: 3,
    }}
  >
    {children}
  </Box>
);

export const CountDivider = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <Typography
      sx={{
        fontSize: "11.5px",
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", background: LINE }} />
  </Box>
);

export const WarningBanner = ({ children }) => (
  <Box
    sx={{
      mb: 3,
      px: 2.5,
      py: 1.75,
      borderRadius: "12px",
      border: "1px solid rgba(138,17,37,0.2)",
      background: "rgba(138,17,37,0.04)",
      color: BRAND,
      fontFamily: '"DM Sans", sans-serif',
      fontSize: "13.5px",
      fontWeight: 500,
    }}
  >
    {children}
  </Box>
);

export const ListShell = ({ children }) => (
  <Box
    sx={{
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
      "& > *:last-child": { borderBottom: "none" },
    }}
  >
    {children}
  </Box>
);

export const ClickableRow = ({ onClick, columns, children }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "grid",
      gridTemplateColumns: columns,
      alignItems: "center",
      gap: 2.5,
      px: 3,
      py: 2.25,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.25s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "16%",
        bottom: "16%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138,17,37,0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": { opacity: 1, transform: "translateX(0)" },
        "& .row-name": { color: BRAND },
      },
    }}
  >
    {children}
  </Box>
);

export const RowName = ({ children }) => (
  <Typography
    className="row-name"
    sx={{
      fontFamily: '"Outfit", sans-serif',
      fontSize: "16px",
      fontWeight: 600,
      color: INK,
      letterSpacing: "-0.01em",
      lineHeight: 1.25,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      transition: "color 0.25s ease",
    }}
  >
    {children}
  </Typography>
);

export const RowChevron = () => (
  <Box
    className="row-arrow"
    sx={{
      width: 34,
      height: 34,
      borderRadius: "50%",
      border: `1px solid ${LINE}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transform: "translateX(-6px)",
      transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
      color: BRAND,
    }}
  >
    <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
  </Box>
);

export const EmptyState = ({ icon: Icon, title, hint }) => (
  <Box
    sx={{
      py: 8,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
    }}
  >
    {Icon && (
      <Box
        sx={{
          width: 60,
          height: 60,
          mx: "auto",
          mb: 2.5,
          borderRadius: "18px",
          background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
        }}
      >
        <Icon sx={{ color: "#fff", fontSize: 28 }} />
      </Box>
    )}
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.35rem",
        fontWeight: 600,
        color: INK,
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography
        sx={{
          fontSize: "14px",
          color: INK_MUTED,
          maxWidth: 380,
          mx: "auto",
          lineHeight: 1.6,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {hint}
      </Typography>
    )}
  </Box>
);

export const RowSkeleton = ({ rows = 3 }) => (
  <ListShell>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Skeleton
          variant="rounded"
          width={44}
          height={44}
          sx={{ borderRadius: "13px" }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="32%" height={18} />
          <Skeleton width="20%" height={13} sx={{ mt: 0.5 }} />
        </Box>
        <Skeleton
          variant="rounded"
          width={80}
          height={26}
          sx={{ borderRadius: "999px" }}
        />
      </Box>
    ))}
  </ListShell>
);
